import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, type DialogProps } from "@mui/material"
import type { FC } from "react"
import { useForm } from "react-hook-form"

import { type AttachFilesFormValues, attachFilesFormSchema } from "./schema"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { MultiFileUpload } from "@/components/upload/MultiFileUpload/MultiFileUpload"
import type { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import type { UploadFileItem } from "@/hooks/useMultiFileUpload"
import { useMultiFileUpload } from "@/hooks/useMultiFileUpload"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { Option } from "@/types/form-fields"

interface AttachFilesDialogProps {
  title: string
  referenceTableName: ReferenceTableName
  referenceId: string
  documentTypeOptions: Option<string>[]
  successMessage: string
  testIdPrefix: string
  dialogTestId: string
  maxWidth?: DialogProps["maxWidth"]
  onClose: () => void
  onSuccess?: () => void
}

export const AttachFilesDialog: FC<AttachFilesDialogProps> = ({
  title,
  referenceTableName,
  referenceId,
  documentTypeOptions,
  successMessage,
  testIdPrefix,
  dialogTestId,
  maxWidth = "lg",
  onClose,
  onSuccess,
}) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<AttachFilesFormValues>({
    defaultValues: { files: [] },
    resolver: zodResolver(attachFilesFormSchema),
    mode: "onChange",
  })

  const { statuses, isUploading, uploadFiles } = useMultiFileUpload({ referenceTableName })

  const handleSubmitData = async (formValues: AttachFilesFormValues): Promise<void> => {
    const items: UploadFileItem[] = formValues.files.map((fileValues, index) => ({
      id: String(index),
      file: fileValues.file,
      fieldGroup: fileValues.documentType as FieldGroupEnum,
      label: fileValues.description,
    }))

    const { failures } = await uploadFiles(items, { referenceIds: [referenceId] })

    if (failures.length === items.length) {
      return
    }

    if (!failures.length) {
      notify({ type: NotificationType.Success, message: successMessage })
    }

    onSuccess?.()
    onClose()
  }

  return (
    <Dialog open onClose={onClose} maxWidth={maxWidth} fullWidth data-testid={dialogTestId}>
      <DialogHeader title={title} onClose={onClose} />
      <DialogContent classes={{ root: "p-10 pb-4" }}>
        <MultiFileUpload
          control={control}
          name='files'
          documentTypeOptions={documentTypeOptions}
          withDescription
          statuses={statuses}
          isUploading={isUploading}
          testIdPrefix={`${testIdPrefix}.multi-file-upload`}
        />
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose} data-testid={`${testIdPrefix}.cancel-button`}>
          Cancel
        </Button>
        <Button
          type='submit'
          loading={isUploading}
          onClick={handleSubmit(handleSubmitData)}
          disabled={!isValid}
          data-testid={`${testIdPrefix}.submit-button`}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}
